import React, { useState, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { 
  Bold, 
  Italic, 
  List, 
  Quote,
  Link2, 
  Strikethrough, 
  ListOrdered, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify, 
  Underline, 
  Superscript, 
  Subscript, 
  Highlighter, 
  ListChecks,
  FileSymlink,
  Heading1,
  Heading2,
  Heading3,
  Type
} from 'lucide-react';
import { knowledgeApi, KnowledgeItem } from '../../services/api';
import { modalManager } from '../../utils/modalManager';

interface ToolbarProps {
  editor: Editor;
}

interface ToolbarButton {
  icon: React.ReactNode;
  label: string;
  action: () => boolean;
  isActive?: () => boolean;
  group?: string;
}

export function Toolbar({ editor }: ToolbarProps) {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showInternalLinkSelector, setShowInternalLinkSelector] = useState(false);
  const [showExternalLinkInput, setShowExternalLinkInput] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<KnowledgeItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Подгрузка списка документов при открытии селектора
  useEffect(() => {
    if (showInternalLinkSelector) {
      loadKnowledgeItems();
    }
  }, [showInternalLinkSelector]);

  // Фильтрация элементов при изменении поискового запроса
  useEffect(() => {
    if (knowledgeItems.length === 0) {
      console.log('[TOOLBAR] Нет элементов для фильтрации');
      setFilteredItems([]);
      return;
    }

    if (searchTerm.trim() === '') {
      console.log('[TOOLBAR] Пустой поисковый запрос, показываем все элементы:', knowledgeItems.length);
      setFilteredItems(knowledgeItems);
    } else {
      const searchTermLower = searchTerm.toLowerCase();
      const filtered = knowledgeItems.filter(item => {
        const name = item.name.toLowerCase();
        // Если запрос начинается с "knowledge", считаем это особым случаем и показываем все файлы
        if (searchTermLower === 'knowledge' || searchTermLower.startsWith('knowledge ')) {
          return true;
        }
        return name.includes(searchTermLower);
      });
      console.log(`[TOOLBAR] Отфильтровано элементов: ${filtered.length} из ${knowledgeItems.length} по запросу "${searchTerm}"`);
      setFilteredItems(filtered);
    }
  }, [searchTerm, knowledgeItems]);

  // Отображаем/скрываем модальное окно выбора внутренней ссылки
  useEffect(() => {
    if (showInternalLinkSelector) {
      console.log('[TOOLBAR] Открываем модальное окно выбора внутренней ссылки. Элементов:', filteredItems.length);
      // Показываем модальное окно через менеджер
      modalManager.showDocumentSelector({
        searchTerm,
        isLoading,
        items: filteredItems,
        onSearchChange: setSearchTerm,
        onItemSelect: createInternalLink,
        onClose: () => {
          console.log('[TOOLBAR] Закрытие модального окна выбора ссылки');
          setIsLoading(false); // Сбрасываем флаг загрузки при закрытии
          setShowInternalLinkSelector(false);
        }
      });
    }
  }, [showInternalLinkSelector]);
  
  // Обновляем опции модального окна при изменении данных
  useEffect(() => {
    if (showInternalLinkSelector) {
      console.log('[TOOLBAR] Обновляем опции модального окна. Загрузка:', isLoading, 'Элементов:', filteredItems.length);
      modalManager.updateOptions({
        searchTerm,
        isLoading,
        items: filteredItems,
        onSearchChange: setSearchTerm,
        onItemSelect: createInternalLink,
        onClose: () => {
          console.log('[TOOLBAR] Закрытие модального окна выбора ссылки');
          setIsLoading(false); // Сбрасываем флаг загрузки при закрытии
          setShowInternalLinkSelector(false);
        }
      });
    }
  }, [searchTerm, isLoading, filteredItems, showInternalLinkSelector]);
  
  // Отображаем/скрываем модальное окно для внешней ссылки
  useEffect(() => {
    if (showExternalLinkInput) {
      modalManager.showExternalLinkInput({
        initialUrl: linkUrl,
        onSubmit: (url) => {
          if (url.trim()) {
            editor.chain().focus().setLink({ href: url.trim() }).run();
          }
          setLinkUrl('');
          setShowExternalLinkInput(false);
        },
        onClose: () => {
          setShowExternalLinkInput(false);
          setLinkUrl('');
        }
      });
    }
  }, [showExternalLinkInput, linkUrl, editor]);

  // Загрузка списка документов
  const loadKnowledgeItems = async () => {
    try {
      setIsLoading(true);
      console.log('[TOOLBAR] Загружаем список документов');
      const items = await knowledgeApi.getItems();
      console.log('[TOOLBAR] Получена структура:', items);
      
      // Преобразуем древовидную структуру в плоский список
      const flattenItems = (items: KnowledgeItem[], path: string = '', result: KnowledgeItem[] = []): KnowledgeItem[] => {
        if (!items || !Array.isArray(items) || items.length === 0) {
          console.log('[TOOLBAR] Нет элементов для обработки в пути:', path);
          return result;
        }
        
        console.log(`[TOOLBAR] Обрабатываем ${items.length} элементов в пути: ${path}`);
        
        for (const item of items) {
          if (!item) {
            console.warn('[TOOLBAR] Обнаружен null/undefined элемент');
            continue;
          }
          
          const fullPath = path ? `${path} / ${item.name}` : item.name;
          
          if (item.itemType === 'file') {
            console.log(`[TOOLBAR] Добавляем файл в результат: ${fullPath}`);
            result.push(item);
          } else {
            console.log(`[TOOLBAR] Обрабатываем папку: ${fullPath}`);
          }
          
          if (item.children && Array.isArray(item.children) && item.children.length > 0) {
            console.log(`[TOOLBAR] Рекурсивно обрабатываем ${item.children.length} дочерних элементов папки ${item.name}`);
            flattenItems(item.children, fullPath, result);
          }
        }
        
        return result;
      };
      
      const flatList = flattenItems(items);
      console.log(`[TOOLBAR] Сформирован плоский список файлов: ${flatList.length} элементов`);
      
      if (flatList.length > 0) {
        console.log('[TOOLBAR] Примеры файлов:');
        flatList.slice(0, 3).forEach((item, index) => {
          console.log(`[TOOLBAR] Файл ${index + 1}: ${item.name} (ID: ${item.id})`);
        });
      } else {
        console.warn('[TOOLBAR] Плоский список файлов пуст!');
      }
      
      // Обновляем локальное состояние
      setKnowledgeItems(flatList);
      setFilteredItems(flatList);
      
      // Принудительно обновляем modalManager без ожидания эффекта
      console.log('[TOOLBAR] Принудительное обновление modalManager с данными:', flatList.length);
      if (showInternalLinkSelector) {
        modalManager.updateOptions({
          searchTerm,
          isLoading: false,  // Явно устанавливаем isLoading в false
          items: flatList,
          onSearchChange: setSearchTerm,
          onItemSelect: createInternalLink,
          onClose: () => {
            console.log('[TOOLBAR] Закрытие модального окна выбора ссылки');
            setIsLoading(false);
            setShowInternalLinkSelector(false);
          }
        });
      }
      
      // После обновления manager устанавливаем состояние isLoading в false
      setIsLoading(false);
    } catch (error) {
      console.error('[TOOLBAR] Ошибка при загрузке элементов базы знаний:', error);
      setIsLoading(false);
    }
  };

  // Функция для создания внутренней ссылки
  const createInternalLink = (item: KnowledgeItem) => {
    if (!item || !item.id) {
      console.error('[TOOLBAR] createInternalLink: Неверные данные элемента.');
      return;
    }
    
    console.log(`[TOOLBAR] createInternalLink: Попытка создать ссылку для ID: ${item.id}, Имя: ${item.name}`);
    
    // Закрываем селектор немедленно
    setShowInternalLinkSelector(false);
    
    // Небольшая задержка перед действиями с редактором
    setTimeout(() => {
      try {
        // --- Создаем стандартный href, а ID храним в data-атрибутах --- 
        const href = `/knowledge/${item.id}`; // Используем стандартный формат
        console.log(`[TOOLBAR] (Delayed) createInternalLink: Сформирован href: ${href}`);
        
        // Проверяем редактор
        if (!editor) {
          console.error('[TOOLBAR] (Delayed) createInternalLink: Редактор не инициализирован.');
          return;
        }
        if (!editor.isEditable) {
          console.error('[TOOLBAR] (Delayed) createInternalLink: Редактор не доступен для редактирования.');
          return;
        }
        
        // --- Явный фокус --- 
        editor.commands.focus();
        console.log(`[TOOLBAR] (Delayed) createInternalLink: Попытка установить фокус. isFocused: ${editor.isFocused}`);
        
        if (!editor.isFocused) {
          console.warn('[TOOLBAR] (Delayed) createInternalLink: Редактор все еще не в фокусе после editor.commands.focus()');
        }
        
        const { state } = editor;
        const { selection } = state;
        const hasSelection = !selection.empty;
        console.log(`[TOOLBAR] (Delayed) createInternalLink: Проверка выделения (hasSelection: ${hasSelection}, from: ${selection.from}, to: ${selection.to}).`);
        
        // --- Подготовка атрибутов ссылки (href стандартный, ID и маркер в data) --- 
        const linkAttributes = {
          href, // Стандартный href
          target: '_self', // Можно оставить или убрать, если не нужно
          'data-internal-link': 'true', // Главный маркер!
          'data-document-id': item.id,
          'data-document-name': item.name
        };
        
        console.log('[TOOLBAR] (Delayed) Атрибуты для ссылки:', linkAttributes);
        
        // --- Применение/Вставка ссылки --- 
        let commandChain = editor.chain();
        
        if (hasSelection) {
          console.log('[TOOLBAR] (Delayed) createInternalLink: Применяем ссылку к существующему выделению.');
          commandChain = commandChain.setLink(linkAttributes);
        } else {
          console.log(`[TOOLBAR] (Delayed) createInternalLink: Вставляем текст "${item.name}" как новую ссылку.`);
          const linkMark = editor.schema.marks.link.create(linkAttributes);
          const textNode = editor.schema.text(item.name, [linkMark]);
          commandChain = commandChain.insertContent(textNode);
        }
        
        // --- Выполнение команды --- 
        const result = commandChain.run();
        console.log(`[TOOLBAR] (Delayed) createInternalLink: Команда ${hasSelection ? 'setLink' : 'insertContent'} выполнена (result: ${result}).`);
        
      } catch (error) {
        console.error('[TOOLBAR] (Delayed) createInternalLink: Ошибка при выполнении команды редактора:', error);
      }
    }, 100);
  };

  const mainControls: ToolbarButton[] = [
    { icon: <Bold className="w-4 h-4" />, label: 'Жирный', action: () => editor.chain().focus().toggleBold().run(), isActive: () => editor.isActive('bold'), group: 'main' },
    { icon: <Italic className="w-4 h-4" />, label: 'Курсив', action: () => editor.chain().focus().toggleItalic().run(), isActive: () => editor.isActive('italic'), group: 'main' },
    { icon: <Underline className="w-4 h-4" />, label: 'Подчеркнутый', action: () => editor.chain().focus().toggleUnderline().run(), isActive: () => editor.isActive('underline'), group: 'main' },
    { icon: <Strikethrough className="w-4 h-4" />, label: 'Зачеркнутый', action: () => editor.chain().focus().toggleStrike().run(), isActive: () => editor.isActive('strike'), group: 'main' }
  ];

  const headingControls: ToolbarButton[] = [
    { icon: <Heading1 className="w-4 h-4" />, label: 'Заголовок 1', action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(), isActive: () => editor.isActive('heading', { level: 1 }), group: 'heading' },
    { icon: <Heading2 className="w-4 h-4" />, label: 'Заголовок 2', action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), isActive: () => editor.isActive('heading', { level: 2 }), group: 'heading' },
    { icon: <Heading3 className="w-4 h-4" />, label: 'Заголовок 3', action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), isActive: () => editor.isActive('heading', { level: 3 }), group: 'heading' },
    { icon: <Type className="w-4 h-4" />, label: 'Обычный текст', action: () => editor.chain().focus().setParagraph().run(), isActive: () => editor.isActive('paragraph'), group: 'heading' }
  ];

  const advancedControls: ToolbarButton[] = [
    { icon: <Superscript className="w-4 h-4" />, label: 'Верхний индекс', action: () => editor.chain().focus().toggleSuperscript().run(), isActive: () => editor.isActive('superscript'), group: 'advanced' },
    { icon: <Subscript className="w-4 h-4" />, label: 'Нижний индекс', action: () => editor.chain().focus().toggleSubscript().run(), isActive: () => editor.isActive('subscript'), group: 'advanced' },
    { icon: <Highlighter className="w-4 h-4" />, label: 'Выделение', action: () => editor.chain().focus().toggleHighlight().run(), isActive: () => editor.isActive('highlight'), group: 'advanced' }
  ];

  const listControls: ToolbarButton[] = [
    { icon: <List className="w-4 h-4" />, label: 'Маркированный список', action: () => editor.chain().focus().toggleBulletList().run(), isActive: () => editor.isActive('bulletList'), group: 'list' },
    { icon: <ListOrdered className="w-4 h-4" />, label: 'Нумерованный список', action: () => editor.chain().focus().toggleOrderedList().run(), isActive: () => editor.isActive('orderedList'), group: 'list' },
    { icon: <ListChecks className="w-4 h-4" />, label: 'Список задач', action: () => editor.chain().focus().toggleTaskList().run(), isActive: () => editor.isActive('taskList'), group: 'list' }
  ];

  const alignmentControls: ToolbarButton[] = [
    { icon: <AlignLeft className="w-4 h-4" />, label: 'По левому краю', action: () => editor.chain().focus().setTextAlign('left').run(), isActive: () => editor.isActive({ textAlign: 'left' }), group: 'align' },
    { icon: <AlignCenter className="w-4 h-4" />, label: 'По центру', action: () => editor.chain().focus().setTextAlign('center').run(), isActive: () => editor.isActive({ textAlign: 'center' }), group: 'align' },
    { icon: <AlignRight className="w-4 h-4" />, label: 'По правому краю', action: () => editor.chain().focus().setTextAlign('right').run(), isActive: () => editor.isActive({ textAlign: 'right' }), group: 'align' },
    { icon: <AlignJustify className="w-4 h-4" />, label: 'По ширине', action: () => editor.chain().focus().setTextAlign('justify').run(), isActive: () => editor.isActive({ textAlign: 'justify' }), group: 'align' }
  ];

  const handleLinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const renderToolbarButton = ({ icon, label, action, isActive }: ToolbarButton) => (
    <button
      key={label}
      onClick={() => action()}
      className={`p-1 rounded transition-colors ${
        isActive?.()
          ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
      title={label}
    >
      {icon}
    </button>
  );

  const handleInternalLinkButtonClick = (e: React.MouseEvent) => {
    console.log('[MODAL DEBUG] Кнопка внутренней ссылки нажата');
    e.preventDefault();
    e.stopPropagation();
    setShowInternalLinkSelector(true);
    setShowLinkInput(false);
    setShowExternalLinkInput(false);
  };
  
  const handleExternalLinkButtonClick = (e: React.MouseEvent) => {
    console.log('[MODAL DEBUG] Кнопка внешней ссылки нажата');
    e.preventDefault();
    e.stopPropagation();
    setShowExternalLinkInput(true);
    setShowLinkInput(false);
    setShowInternalLinkSelector(false);
  };

  return (
    <div className="rounded flex items-center flex-wrap gap-1 py-0.5 px-1 mb-0.5 relative">
      <div className="flex items-center gap-1">
        {mainControls.map(renderToolbarButton)}
      </div>

      <div className="flex items-center gap-1 pl-1 border-l border-gray-200 dark:border-gray-700">
        {headingControls.map(renderToolbarButton)}
      </div>

      <div className="flex items-center gap-1 pl-1 border-l border-gray-200 dark:border-gray-700">
        {advancedControls.map(renderToolbarButton)}
      </div>

      <div className="flex items-center gap-1 pl-1 border-l border-gray-200 dark:border-gray-700">
        {listControls.map(renderToolbarButton)}
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-1 rounded transition-colors ${
            editor.isActive('blockquote')
              ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          title="Цитата"
        >
          <Quote className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-1 pl-1 border-l border-gray-200 dark:border-gray-700">
        {alignmentControls.map(renderToolbarButton)}
      </div>

      <div className="flex items-center gap-1 pl-1 border-l border-gray-200 dark:border-gray-700">
        <button
          onClick={handleExternalLinkButtonClick}
          className={`p-1 rounded transition-colors ${
            editor.isActive('link') && !editor.getAttributes('link').href?.startsWith('internal://')
              ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          title="Внешняя ссылка"
        >
          <Link2 className="w-4 h-4" />
        </button>
        
        <button
          onClick={handleInternalLinkButtonClick}
          className={`p-1 rounded transition-colors ${
            editor.isActive('link') && editor.getAttributes('link').href?.startsWith('internal://')
              ? 'bg-indigo-100 dark:bg-indigo-900/20 text-[#4e4ae7] dark:text-[#4e4ae7]'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          title="Внутренняя ссылка на документ"
        >
          <FileSymlink className="w-4 h-4" />
        </button>
      </div>

      {showLinkInput && (
        <div className="p-1 rounded flex flex-1">
          <form onSubmit={handleLinkSubmit} className="flex gap-1 w-full">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs"
            />
            <button
              type="submit"
              className="px-2 py-1 bg-blue-600 text-white rounded text-xs"
            >
              OK
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
